import axios from "axios";
import FalconLightBox from "components/common/FalconLightBox";
import Flex from "components/common/Flex";
import QuillEditor from "components/common/QuillEditor";
import SubtleBadge from "components/common/SubtleBadge";
import JoditTextEditor from "components/examples/JoditEditor/JoditTextEditor";
import SunTextEditor from "components/examples/JoditEditor/JoditTextEditor";
import countries from "data/countries";
import { useFetchCategoriesGroup } from "hooks/useFetchCategoriesGroup";
import { useFetchPageGroups } from "hooks/useFetchPagesGroup";
import JoditEditor from "jodit-react";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Form, Image, Row } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { FaImage } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { BASEURL, customSettings } from "utils/customConfig";
import CustomOption from "utils/CustomOption";

const CostCenterBasicInformation = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const {
    register,
    setValue,
    control,
    watch,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [loading, setLoading] = useState(false);
  const maxCharactersLength = 255;
  const [businessDomain, setBusinessDomain] = useState(
    getValues("businessDomain") || []
  );
  const [inputValue, setInputValue] = useState("");

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && inputValue) {
      event.preventDefault();
      if (!businessDomain?.includes(inputValue?.trim())) {
        setBusinessDomain([...businessDomain, inputValue?.trim()]);
      }
      setInputValue("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setBusinessDomain(businessDomain?.filter((tag) => tag !== tagToRemove));
  };

  useEffect(() => {
    if (state) {
      setBusinessDomain(state?.businessDomain || []);
    }
  }, [state, setValue]);

  useEffect(() => {
    // Update form value for tags when local state changes
    setValue("businessDomain", businessDomain);
  }, [businessDomain, setValue]);

  return (
    <>
      {loading ? (
        <div className="d-flex py-3 justify-content-center align-items-center">
          <Button variant="falcon-default" size="sm" className="fs-10">
            Loading...
          </Button>
        </div>
      ) : (
        <Card.Body
          className=""
          style={{
            height: `calc(100vh - 12.7rem)`,
            overflow: "auto",
            padding: "5px 20px",
          }}
        >
          <Row className="gx-2 pb-2">
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Code"
                  title="Code"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.code}
                  {...register("code")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.code?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  title="Name"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.name}
                  {...register("name")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="Default Currency"
                  type="text"
                  title="Default Currency"
                  className="py-2 mb-1"
                  isInvalid={!!errors?.currency}
                  {...register("currency")}
                >
                  <option value="">Default Currency</option>
                  <option value="Rs.">Rs. Pakistan Rupe</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.currency?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="Country"
                  type="text"
                  title="Country"
                  className="py-2 mb-1"
                  isInvalid={!!errors?.country}
                  {...register("country")}
                >
                  <option value="">Select Country</option>
                  {countries.map((country, index) => (
                    <option key={index} value={country}>
                      {country}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.country?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Address"
                  title="Address"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.address}
                  {...register("address")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.address?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Contact"
                  title="Contact"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.contact}
                  {...register("contact")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.contact?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>

            <Col md="6">
              <div className="form-control d-flex flex-wrap align-items-center mb-1">
                {businessDomain?.map((tag, index) => (
                  <SubtleBadge key={index} className=" mr-2 mb-1 mx-1">
                    {tag}
                    <span
                      className="ml-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleRemoveTag(tag)}
                    >
                      &times;
                    </span>
                  </SubtleBadge>
                ))}
                <input
                  type="text"
                  title="Business Domain"
                  className="border-0 flex-grow-1"
                  style={{
                    outline: "none",
                    color: "black",
                    minWidth: "120px",
                    paddingBottom: "3px",
                    paddingTop: "3px",
                  }}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Business Domain"
                />
              </div>
            </Col>
            <Col md="6">
              <Form.Check
                type="checkbox"
                id="defaultSwitch"
                label="Is Group"
                className="mt-2"
                defaultChecked={!state}
                isInvalid={!!errors.isGroup}
                {...register("isGroup")}
              />
            </Col>
          </Row>
        </Card.Body>
      )}
    </>
  );
};

export default React.memo(CostCenterBasicInformation);
